import { 
  getAllUsers, 
  getUserById, 
  updateUserData, 
  logAdminActivity,
  getActivityLogs,
  checkAdminStatus,
  getAllAdmins
} from '../firebase/helpers';
import { auth } from '../firebase/config';

/**
 * Verify admin status before performing admin operations
 * Throws error if user is not authenticated or not an admin
 */
export const verifyAdminAccess = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Unauthorized: Not authenticated');
  }

  const adminStatus = await checkAdminStatus(currentUser.uid, currentUser.email);
  if (!adminStatus.isAdmin) {
    throw new Error('Unauthorized: Admin access required');
  }

  return {
    adminId: currentUser.uid,
    adminEmail: currentUser.email
  };
};

/**
 * Get all users for admin panel
 * Requires admin authentication
 */
export const fetchAllUsers = async () => {
  try {
    await verifyAdminAccess();
    const users = await getAllUsers();
    
    // Log the activity
    const adminInfo = await verifyAdminAccess();
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'fetchAllUsers',
      targetUserId: null,
      targetUserEmail: null,
      changes: { count: users.length },
      reason: 'Admin viewed all users',
      ipAddress: null
    });
    
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Get all regular users (excluding admin users)
 * Requires admin authentication
 * Filters out users who are admins (either in adminUsers collection or have admin role)
 */
export const fetchAllRegularUsers = async () => {
  try {
    await verifyAdminAccess();
    
    // Get all users and all admins
    const [allUsers, allAdmins] = await Promise.all([
      getAllUsers(),
      getAllAdmins()
    ]);
    
    // Create a Set of admin UIDs for fast lookup
    const adminUids = new Set(allAdmins.map(admin => admin.id));
    
    // Filter out admin users
    // A user is considered an admin if:
    // 1. Their UID exists in adminUsers collection, OR
    // 2. They have role === 'admin' or role === 'superAdmin' or admin === true
    const regularUsers = allUsers.filter(user => {
      // Check if user is in adminUsers collection
      if (adminUids.has(user.id)) {
        return false;
      }
      
      // Check if user has admin role flags
      if (user.role === 'admin' || user.role === 'superAdmin' || user.admin === true) {
        return false;
      }
      
      return true;
    });
    
    // Log the activity
    const adminInfo = await verifyAdminAccess();
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'fetchAllRegularUsers',
      targetUserId: null,
      targetUserEmail: null,
      changes: { 
        totalUsers: allUsers.length,
        regularUsers: regularUsers.length,
        adminUsers: allUsers.length - regularUsers.length
      },
      reason: 'Admin viewed regular users (excluding admins)',
      ipAddress: null
    });
    
    return regularUsers;
  } catch (error) {
    console.error('Error fetching regular users:', error);
    throw error;
  }
};

/**
 * Get single user by ID
 * Requires admin authentication
 */
export const fetchUserById = async (userId) => {
  try {
    await verifyAdminAccess();
    const user = await getUserById(userId);
    
    // Log the activity
    const adminInfo = await verifyAdminAccess();
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'viewUser',
      targetUserId: userId,
      targetUserEmail: user?.email || 'N/A',
      changes: {},
      reason: 'Admin viewed user details',
      ipAddress: null
    });
    
    return user;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Update user data with audit logging
 * Requires admin authentication and logs all changes
 */
export const updateUser = async (userId, updates, reason = '') => {
  try {
    // Verify admin access
    const adminInfo = await verifyAdminAccess();

    // Validate updates - prevent direct modification of sensitive fields without proper logging
    const sensitiveFields = ['role', 'admin', 'isDeleted'];
    const hasSensitiveChanges = Object.keys(updates).some(key => sensitiveFields.includes(key));
    
    if (hasSensitiveChanges && !reason) {
      throw new Error('Reason is required when modifying sensitive fields (role, admin, isDeleted)');
    }

    // Get current user data (before state)
    const beforeData = await getUserById(userId);
    
    if (!beforeData) {
      throw new Error('User not found');
    }
    
    // Perform update
    await updateUserData(userId, updates);
    
    // Get updated user data (after state)
    const afterData = await getUserById(userId);
    
    // Identify all changes (including nested objects)
    const changes = {};
    const identifyChanges = (before, after, prefix = '') => {
      const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
      allKeys.forEach(key => {
        const beforeVal = before?.[key];
        const afterVal = after?.[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        if (typeof beforeVal === 'object' && typeof afterVal === 'object' && 
            beforeVal !== null && afterVal !== null && 
            !Array.isArray(beforeVal) && !Array.isArray(afterVal)) {
          identifyChanges(beforeVal, afterVal, fullKey);
        } else if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
          changes[fullKey] = {
            before: beforeVal,
            after: afterVal
          };
        }
      });
    };
    
    identifyChanges(beforeData, afterData);
    
    // Log admin activity with comprehensive audit trail
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'updateUser',
      targetUserId: userId,
      targetUserEmail: afterData?.email || beforeData?.email || 'N/A',
      changes: changes,
      reason: reason || 'No reason provided',
      ipAddress: null,
      metadata: {
        fieldsModified: Object.keys(updates),
        hasSensitiveChanges: hasSensitiveChanges
      }
    });
    
    return afterData;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Get activity logs
 * Requires admin authentication
 */
export const fetchActivityLogs = async (filters = {}) => {
  try {
    await verifyAdminAccess();
    const logs = await getActivityLogs(filters);
    
    // Log the activity (viewing logs)
    const adminInfo = await verifyAdminAccess();
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'viewActivityLogs',
      targetUserId: null,
      targetUserEmail: null,
      changes: { filters, logCount: logs.length },
      reason: 'Admin viewed activity logs',
      ipAddress: null
    });
    
    return logs;
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
};

/**
 * Delete user with audit logging
 * Completely removes user from Firestore
 * Requires admin authentication and reason
 */
export const deleteUser = async (userId, reason = '') => {
  try {
    // Verify admin access
    const adminInfo = await verifyAdminAccess();

    // Require reason for deletion
    if (!reason || reason.trim() === '') {
      throw new Error('Reason is required for user deletion');
    }

    // Prevent self-deletion
    if (userId === adminInfo.adminId) {
      throw new Error('Cannot delete your own admin account');
    }

    // Get user data before deletion (for logging)
    const userData = await getUserById(userId);
    
    if (!userData) {
      throw new Error('User not found');
    }

    // Log admin activity BEFORE deletion (so we have the data)
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'deleteUser',
      targetUserId: userId,
      targetUserEmail: userData.email || 'N/A',
      changes: {
        deleted: {
          before: {
            email: userData.email,
            username: userData.username,
            vipLevel: userData.vipLevel,
            balances: userData.balances,
            createdAt: userData.createdAt
          },
          after: null
        }
      },
      reason: reason.trim(),
      ipAddress: null,
      metadata: {
        deletedAt: new Date().toISOString(),
        deletedBy: adminInfo.adminEmail
      }
    });

    // Completely delete user document from Firestore
    const { deleteDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
    
    // Note: Firebase Authentication user deletion requires Admin SDK
    // For now, we delete the Firestore document which will trigger logout
    // The user won't be able to login because their profile won't exist
    
    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Verify admin status - checks both adminUsers and users collections
 * Returns detailed status information
 */
export const verifyAdmin = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { isAdmin: false, error: 'Not authenticated' };
    }
    
    // Use the updated checkAdminStatus function
    const adminStatus = await checkAdminStatus(currentUser.uid, currentUser.email);
    return {
      isAdmin: adminStatus.isAdmin,
      source: adminStatus.source,
      requiresUidFix: adminStatus.requiresUidFix,
      message: adminStatus.message
    };
  } catch (error) {
    console.error('Error verifying admin:', error);
    return { isAdmin: false, error: error.message };
  }
};

/**
 * Process a deposit for a user
 * Adds funds to user's wallet balance and logs the transaction
 */
export const processDeposit = async (userId, amount, reason = '') => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    if (!amount || amount <= 0) {
      throw new Error('Deposit amount must be greater than 0');
    }

    // Get current user data
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Calculate new balances
    const currentWallet = user.balances?.wallet || 0;
    const currentCommission = user.balances?.commission || 0;
    // Calculate previous total: Total = Wallet + Commission (commission is included in wallet display)
    const currentTotal = currentWallet + currentCommission;
    const newWallet = currentWallet + amount;
    // Total = Wallet + Commission (commission is included in wallet, so total includes commission)
    const newTotal = newWallet + currentCommission;

    // Update user balances
    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: newWallet,
        total: newTotal
      }
    });

    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'processDeposit',
      targetUserId: userId,
      targetUserEmail: user.email,
      changes: {
        depositAmount: amount,
        previousWallet: currentWallet,
        newWallet: newWallet,
        previousTotal: currentTotal,
        newTotal: newTotal
      },
      reason: reason || `Deposit of ${amount} USDT processed`,
      ipAddress: null
    });

    return {
      success: true,
      message: `Deposit of ${amount} USDT processed successfully`,
      newBalance: newWallet,
      newTotal: newTotal
    };
  } catch (error) {
    console.error('Error processing deposit:', error);
    throw error;
  }
};

/**
 * Get deposit history for a user
 */
export const getDepositHistory = async (userId) => {
  try {
    await verifyAdminAccess();
    
    // Get all activity logs and filter client-side
    const allLogs = await getActivityLogs({ limit: 200 });
    
    // Filter for deposits for this specific user
    const depositLogs = allLogs.filter(log => 
      log.action === 'processDeposit' && 
      log.targetUserId === userId
    );

    return depositLogs.map(log => ({
      id: log.id,
      amount: log.changes?.depositAmount || 0,
      previousBalance: log.changes?.previousWallet || 0,
      newBalance: log.changes?.newWallet || 0,
      processedBy: log.adminEmail,
      reason: log.reason,
      timestamp: log.timestamp
    }));
  } catch (error) {
    console.error('Error fetching deposit history:', error);
    throw error;
  }
};

/**
 * Get comprehensive user statistics (excluding admin users)
 * Returns totalUsers, activeUsers, totalWalletBalance, totalCommission, vipDistribution
 */
export const getUserStatistics = async () => {
  try {
    await verifyAdminAccess();
    
    // Get all users and all admins
    const [allUsers, allAdmins] = await Promise.all([
      getAllUsers(),
      getAllAdmins()
    ]);
    
    // Create a Set of admin UIDs for fast lookup
    const adminUids = new Set(allAdmins.map(admin => admin.id));
    
    // Filter out admin users - only include regular users
    const regularUsers = allUsers.filter(user => {
      // Check if user is in adminUsers collection
      if (adminUids.has(user.id)) {
        return false;
      }
      
      // Check if user has admin role flags
      if (user.role === 'admin' || user.role === 'superAdmin' || user.admin === true) {
        return false;
      }
      
      return true;
    });
    
    const validUsers = regularUsers.filter(user => !user.isDeleted);
    const activeUsers = validUsers.filter(user => user.isActive !== false);
    
    // Calculate financial totals
    let totalWalletBalance = 0;
    let totalCommission = 0;
    let totalFrozen = 0;
    let totalBalance = 0;
    
    validUsers.forEach(user => {
      const balances = user.balances || {};
      totalWalletBalance += balances.wallet || 0;
      totalCommission += balances.commission || 0;
      totalFrozen += balances.frozen || 0;
      totalBalance += balances.total || 0;
    });
    
    // Calculate VIP distribution
    const vipDistribution = {
      VIP1: validUsers.filter(u => u.vipLevel === 'VIP1').length,
      VIP2: validUsers.filter(u => u.vipLevel === 'VIP2').length,
      VIP3: validUsers.filter(u => u.vipLevel === 'VIP3').length,
      VIP4: validUsers.filter(u => u.vipLevel === 'VIP4').length
    };
    
    const totalVipUsers = Object.values(vipDistribution).reduce((sum, count) => sum + count, 0);
    
    // Calculate percentages
    const vipPercentages = {
      VIP1: totalVipUsers > 0 ? (vipDistribution.VIP1 / totalVipUsers) * 100 : 0,
      VIP2: totalVipUsers > 0 ? (vipDistribution.VIP2 / totalVipUsers) * 100 : 0,
      VIP3: totalVipUsers > 0 ? (vipDistribution.VIP3 / totalVipUsers) * 100 : 0,
      VIP4: totalVipUsers > 0 ? (vipDistribution.VIP4 / totalVipUsers) * 100 : 0
    };
    
    return {
      totalUsers: validUsers.length,
      activeUsers: activeUsers.length,
      inactiveUsers: validUsers.length - activeUsers.length,
      totalWalletBalance,
      totalCommission,
      totalFrozen,
      totalBalance,
      vipDistribution,
      vipPercentages,
      averageWalletBalance: validUsers.length > 0 ? totalWalletBalance / validUsers.length : 0,
      averageCommission: validUsers.length > 0 ? totalCommission / validUsers.length : 0
    };
  } catch (error) {
    console.error('Error getting user statistics:', error);
    throw error;
  }
};

/**
 * Get recent activity from admin activity logs
 * Returns recent system activities with timestamps
 */
export const getRecentActivity = async (limit = 10) => {
  try {
    await verifyAdminAccess();
    const logs = await getActivityLogs({ limit });
    
    return logs.map(log => ({
      id: log.id,
      action: log.action,
      adminEmail: log.adminEmail,
      targetUserEmail: log.targetUserEmail,
      timestamp: log.timestamp,
      reason: log.reason,
      changes: log.changes
    }));
  } catch (error) {
    console.error('Error getting recent activity:', error);
    throw error;
  }
};

/**
 * Get system statistics
 * Returns server status, response time, system version
 */
export const getSystemStats = async () => {
  try {
    await verifyAdminAccess();
    
    // Get collection counts
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const [usersSnapshot, adminsSnapshot, logsSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'adminUsers')),
      getDocs(collection(db, 'adminActivityLog'))
    ]);
    
    return {
      serverStatus: 'online',
      responseTime: Date.now(),
      systemVersion: '1.0.0',
      totalUsers: usersSnapshot.size,
      totalAdmins: adminsSnapshot.size,
      totalActivityLogs: logsSnapshot.size,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error getting system stats:', error);
    throw error;
  }
};

